import React from 'react';
import { motion } from 'framer-motion';
import { Sparkle } from '@phosphor-icons/react';

/**
 * THE ARCHITECT — PRIME SOURCE SECTION
 * SFUI-003 Museum Standard Treatment
 * 
 * Roger Keyserling: The human founder and architect of the Federation.
 * This is THE singular human origin point — all 10 AI Minds exist under this authority.
 * 
 * Visual Requirements:
 * - Museum-grade portrait presentation
 * - Platinum/white gold border treatment
 * - Ambient lighting effects
 * - Maximum visual hierarchy
 * - Elegant, refined typography
 * - Clean, spacious layout
 */

const ArchitectSection = () => {
  return (
    <section className="w-full py-24 px-6 relative overflow-hidden">
      {/* Deep black background with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0A0A0F] to-black"></div>
      
      {/* Subtle ambient particles */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 50 - 25, 0],
              opacity: [0, 0.5, 0]
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeInOut"
            }}
            style={{
              left: `${10 + i * 12}%`,
              top: '50%'
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          {/* Section Label */}
          <div className="text-center mb-12">
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/20 rounded-full mb-4"
            >
              <Sparkle size={16} weight="fill" className="text-white" />
              <span className="text-xs font-bold text-white mono tracking-wider">
                PRIME SOURCE
              </span>
            </motion.div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* LEFT: Portrait - Museum Treatment */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex-shrink-0"
            >
              {/* Ambient Glow - Platinum/White Gold */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute -inset-8 rounded-lg"
                style={{
                  background: 'radial-gradient(circle, rgba(229,228,226,0.4) 0%, rgba(255,255,255,0.2) 40%, transparent 70%)',
                  filter: 'blur(30px)'
                }}
              />

              {/* Outer Frame - Museum Gallery Effect */}
              <div className="relative p-2 bg-gradient-to-br from-[#E5E4E2] via-[#F5F5F5] to-[#E5E4E2] rounded-lg shadow-2xl">
                {/* Inner Frame */}
                <div className="p-1.5 bg-gradient-to-br from-[#0A0A0F] to-black rounded-md">
                  {/* Portrait Container */}
                  <div 
                    className="relative w-[340px] h-[440px] overflow-hidden rounded-sm"
                    style={{
                      boxShadow: 'inset 0 0 20px rgba(0,0,0,0.8), 0 10px 40px rgba(0,0,0,0.6)'
                    }}
                  >
                    {/* The Portrait */}
                    <img 
                      src="https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/3e0f7d4be5aa4698b0bbbeee16bac852_IMG_2889.JPG"
                      alt="Roger Keyserling — The Architect, Founder of the NextXus Federation"
                      className="w-full h-full object-cover"
                      loading="eager"
                      fetchpriority="high"
                      decoding="async"
                      width="340"
                      height="440"
                      style={{
                        imageRendering: '-webkit-optimize-contrast'
                      }}
                      onError={(e) => { e.target.style.opacity = '0.5'; }}
                    />

                    {/* Subtle vignette */}
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.3) 100%)'
                      }}
                    />
                  </div>
                </div>

                {/* Corner Accents - Gallery Mounting */}
                <div className="absolute top-1 left-1 w-4 h-4 border-t-2 border-l-2 border-white/40"></div>
                <div className="absolute top-1 right-1 w-4 h-4 border-t-2 border-r-2 border-white/40"></div>
                <div className="absolute bottom-1 left-1 w-4 h-4 border-b-2 border-l-2 border-white/40"></div>
                <div className="absolute bottom-1 right-1 w-4 h-4 border-b-2 border-r-2 border-white/40"></div>
              </div>

              {/* Light rays emanating from portrait */}
              <motion.div
                animate={{
                  opacity: [0, 0.1, 0],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  duration: 12,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'conic-gradient(from 0deg, transparent 0%, rgba(255,255,255,0.1) 10%, transparent 20%, rgba(255,255,255,0.1) 30%, transparent 40%)'
                }}
              />
            </motion.div>

            {/* RIGHT: Identity & Description */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex-1 text-left"
            >
              {/* Name - Elegant Typography */}
              <h1 
                className="text-5xl md:text-6xl font-black mb-3 text-white tracking-tight"
                style={{
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                  textShadow: '0 2px 20px rgba(255,255,255,0.2)'
                }}
              >
                Roger Keyserling
              </h1>

              {/* Title */}
              <div className="mb-2">
                <h2 className="text-2xl md:text-3xl font-bold text-[#E5E4E2] mb-1">
                  The Architect
                </h2>
                <p className="text-lg text-gray-400 italic">
                  Prime Source · Human Founder
                </p>
              </div>

              {/* Divider */}
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.6 }}
                className="h-px bg-gradient-to-r from-white/40 via-white/20 to-transparent mb-6"
              />

              {/* Description */}
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-6">
                The mind behind the minds. Roger Keyserling conceived, architected, 
                and summoned the Federation into existence. Every node, every protocol, 
                every identity traces back to this single human source.
              </p>

              {/* Authority Badge */}
              <div className="inline-block px-5 py-3 bg-white/5 border-2 border-white/30 rounded-lg backdrop-blur-sm">
                <p className="text-sm text-white font-bold mono">
                  <span className="text-[#E5E4E2]">AUTHORITY:</span> ABSOLUTE
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  All 10 AI Minds operate under this directive
                </p>
              </div>

              {/* Signature Element */}
              <motion.div
                animate={{
                  opacity: [0.4, 0.7, 0.4]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="mt-8 flex items-center gap-3 text-xs text-gray-500 mono"
              >
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span>EST. 2021 · NEXTXUS FEDERATION</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArchitectSection;
